import React, { useState } from "react";
import {
  IconReportAnalytics,
  IconDownload,
  IconPrinter,
  IconFilter,
  IconCalendar,
  IconFileSpreadsheet,
  IconSearch,
  IconBuildingStore
} from "@tabler/icons-react";
import { useAssetReports, useAssetCategories } from "../../controllers/assets.controller";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";

export default function AssetReportsTab() {
  const [reportType, setReportType] = useState("asset_register");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [search, setSearch] = useState("");

  const { data: reportData, error, isLoading } = useAssetReports(reportType, {
    start_date: startDate,
    end_date: endDate,
    category_id: selectedCategory !== "all" ? selectedCategory : undefined
  });

  const { data: categories } = useAssetCategories();

  const reportOptions = [
    { id: "asset_register", name: "1. Master Asset Register (Comprehensive)" },
    { id: "location_register", name: "2. Asset Register by Location (Floor & Room)" },
    { id: "category_register", name: "3. Asset Register by Category" },
    { id: "custodian_register", name: "4. Asset Register by Custodian / Staff" },
    { id: "acquisitions", name: "5. Additions & Acquisitions Report" },
    { id: "transfers", name: "6. Movement & Custody Transfer History" },
    { id: "damage_reports", name: "7. Damage Incident & Defect Log" },
    { id: "maintenance_history", name: "8. Maintenance & Work Order Log" },
    { id: "maintenance_cost", name: "9. Maintenance Cost Analysis" },
    { id: "preventive_compliance", name: "10. Preventive Maintenance Compliance" },
    { id: "verification_audit", name: "11. Physical Audit Discrepancy & Reconciliation" },
    { id: "depreciation_schedule", name: "12. Depreciation Schedule" },
    { id: "fixed_asset_gl", name: "13. Fixed Asset GL Roll-Forward" },
    { id: "disposals", name: "14. Disposals, Write-offs & Gain/Loss" },
    { id: "warranty_expiry", name: "15. Warranty & AMC Expiration Schedule" },
    { id: "replacement_forecast", name: "16. Capital Replacement & EOL Forecast" },
    { id: "fully_depreciated", name: "17. Fully Depreciated Assets In Service" }
  ];

  const rows = Array.isArray(reportData)
    ? reportData.filter((r) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return Object.values(r).some((val) => String(val || "").toLowerCase().includes(q));
      })
    : [];

  const handleExportExcel = () => {
    if (rows.length === 0) {
      toast.error("No data to export");
      return;
    }

    try {
      const currentReportObj = reportOptions.find((r) => r.id === reportType);
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Report");
      XLSX.writeFile(wb, `${reportType}_report_${new Date().toISOString().split("T")[0]}.xlsx`);
      toast.success("Excel report downloaded successfully!");
    } catch (err) {
      toast.error("Export failed");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Determine dynamic columns from data
  const columns = rows.length > 0 ? Object.keys(rows[0]).filter((c) => c !== "tenant_id" && c !== "id") : [];

  return (
    <div className="space-y-6">
      {/* Report Selector & Filter Bar */}
      <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm space-y-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <IconReportAnalytics className="w-6 h-6 text-restro-green" />
              Hotel Asset Intelligence & Statutory Reports
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Audit-ready reports covering valuation, GL roll-forwards, maintenance ROI, and warranty compliance.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-xl transition"
            >
              <IconPrinter className="w-4 h-4" />
              Print
            </button>
            <button
              onClick={handleExportExcel}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-restro-green hover:bg-emerald-600 rounded-xl shadow-sm transition"
            >
              <IconFileSpreadsheet className="w-4 h-4" />
              Export to Excel
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 pt-3 border-t border-gray-100 dark:border-gray-700">
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
              Select Report Type
            </label>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green font-semibold text-gray-900 dark:text-white"
            >
              {reportOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
              Category Filter
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
            >
              <option value="all">All Categories</option>
              {categories?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
              Search Within Report
            </label>
            <div className="relative">
              <IconSearch className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search rows..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                From Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-2.5 py-2 text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-800 dark:text-gray-200"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                To Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-2.5 py-2 text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-800 dark:text-gray-200"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Report Data Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-gray-50/50 dark:bg-gray-750">
          <div className="font-bold text-gray-900 dark:text-white">
            {reportOptions.find((r) => r.id === reportType)?.name}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Showing {rows.length} records
          </div>
        </div>

        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="w-full text-left text-xs text-gray-600 dark:text-gray-300">
            <thead className="bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-200 uppercase font-semibold sticky top-0 shadow-sm">
              <tr>
                {columns.map((col) => (
                  <th key={col} className="px-5 py-3 whitespace-nowrap">
                    {col.replace(/_/g, " ")}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {isLoading ? (
                <tr>
                  <td colSpan={columns.length || 5} className="text-center py-10 text-gray-400 text-sm">
                    Generating report data...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length || 5} className="text-center py-12 text-gray-400 text-sm">
                    No data matching the selected report and date criteria.
                  </td>
                </tr>
              ) : (
                rows.map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-50/50 dark:hover:bg-gray-750 transition">
                    {columns.map((col) => {
                      const val = row[col];
                      const isCostCol =
                        col.includes("cost") ||
                        col.includes("value") ||
                        col.includes("price") ||
                        col.includes("depreciation") ||
                        col.includes("amount") ||
                        col.includes("gain") ||
                        col.includes("loss");
                      return (
                        <td key={col} className="px-5 py-3 whitespace-nowrap">
                          {isCostCol && typeof val === "number" ? (
                            <span className="font-semibold text-gray-900 dark:text-white">
                              Rs. {Number(val).toLocaleString()}
                            </span>
                          ) : typeof val === "string" && val.includes("T00:00:00") ? (
                            new Date(val).toLocaleDateString()
                          ) : (
                            String(val ?? "-")
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
